(() => {
  "use strict";

  /**
   * Room: Living room | Bedroom | Entertainment room | Study Room
   * Use: Movies & streaming | Gaming | Sports & live TV | Everyday everything
   * Size: Small 32–43″ | Medium 55–65″ | Large 75–86″ | SuperSized 100″
   * Picture: Infinite contrast (OLED) | Extreme brightness (Mini-LED) | Wide colour gamut (QLED+) | Good standard picture
   */

  const PLACEHOLDER_IMAGE = "https://placehold.co/1000x500";
  const PLACEHOLDER_ICON = "https://placehold.co/50";
  const TYPES = {
    room: {
      living_room: "Living room",
      bedroom: "Bedroom",
      entertainment_room: "Entertainment room",
      study: "Study Room",
    },
    use: {
      movies: "Movies & streaming",
      gaming: "Gaming",
      sports: "Sports & live TV",
      everyday: "Everyday everything",
    },
    size: {
      small: "Small 32–43″",
      medium: "Medium 55–65″",
      large: "Large 75–86″",
      xlarge: "SuperSized 100″",
    },
    picture: {
      oled: "Infinite contrast (OLED)",
      mini_led: "Extreme brightness (Mini-LED)",
      qled: "Wide colour gamut (QLED+)",
      standard: "Good standard picture",
    },
    features: {
      "144Hz": "144Hz VRR",
      hdmi: "HDMI 2.1 (×3 ports)",
      oled_panel: "OLED panel",
      miniled_local_dimming: "Mini-LED local dimming zones",
      filmmaker_mode: "Filmmaker Mode",
      dolby_vision: "Dolby Vision IQ",
      dolby_atmos: "Dolby Atmos audio",
      dts_trusurround: "DTS TruSurround",
      airplay: "AirPlay 2",
      ai_picture: "AI Picture Processor",
      eye_care: "Eye Care certified",
      memc: "MEMC motion smoothing",
    },
  };
  TYPES.room.all = Object.values(TYPES.room);
  TYPES.use.all = Object.values(TYPES.use);
  TYPES.size.all = Object.values(TYPES.size);
  TYPES.picture.all = Object.values(TYPES.picture);
  const IMAGES = {
    // ─────────── Step 1: Room ───────────
    "Living room":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_4f5f58c4-6c18-487c-ae71-a9691c4faaa9.jpg?v=1782873780",
    Bedroom:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/270fadd6e4129612a304bd5ee90999434aca8739.png?v=1780480446",
    "Entertainment room":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_528688df-ae7f-47b4-b273-ea6fbdd9d6d6.jpg?v=1782874278",
    "Study Room":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_545900a5-7962-46da-b861-92dd946a6dd9.jpg?v=1782874278",

    // ─────────── Step 2: Room | Size ───────────
    // Placeholders — drop final artwork in per room × size (4 × 4 = 16).
    "Living room | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Small_d8c79379-b1c1-4e73-928b-d640e3887164.png?v=1783067735",
    "Living room | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Medium_1d6aef9c-0cb8-42be-8b33-dbce9db33311.png?v=1783064623",
    "Living room | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Large_ec52bf3a-ec54-44cf-8750-7950b39d2502.png?v=1783064622",
    "Living room | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_f49b01c9-de4b-482a-b0d8-eb9e80c55d3e.png?v=1783064623",

    "Bedroom | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Small_9c59878c-177d-4144-b5ff-2a705cffdc99.png?v=1783062800",
    "Bedroom | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_e951b7f1-421a-4415-b9eb-9677a47bb4b1.png?v=1783062800",
    "Bedroom | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_7a1ea6f8-66a1-437e-a9e9-e4fcab08e7b7.png?v=1783062800",
    "Bedroom | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_9e11c586-ca8f-4b0b-a0b2-a7858872bf8a.png?v=1783062801",

    "Entertainment room | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Small_63d6606c-4f02-49b8-99ef-3a58dd2b59d4.png?v=1783063394",
    "Entertainment room | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Medium_dddaaf2e-0dea-4075-9956-4d3d7e149da5.png?v=1783063394",
    "Entertainment room | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Large_d444d860-8311-4699-90c7-ade90d2f9eca.png?v=1783063393",
    "Entertainment room | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_SuperSized_4869239b-07da-4ebb-a55f-9d9e85c623af.png?v=1783063394",

    "Study Room | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Small_080eb8ff-53a7-4501-9cb3-7650c993d500.png?v=1783067710",
    "Study Room | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_d9bd7e3c-16cf-442f-8d4b-2b79f0dbb507.png?v=1783064351",
    "Study Room | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_28b8e212-a41f-4fbe-a3f2-413fe8f2fc4f.png?v=1783064350",
    "Study Room | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_d7e233a4-32b2-4f73-a946-06066efd35e1.png?v=1783064350",

    // ─────────── Step 3: Room | Use | Size ───────────
    "Living room | Movies & streaming | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Small_Movies.jpg?v=1783067830",
    "Living room | Movies & streaming | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Medium_Movies.jpg?v=1783064656",
    "Living room | Movies & streaming | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Large_Movies.jpg?v=1783064655",
    "Living room | Movies & streaming | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_Movies.jpg?v=1783064656",

    "Living room | Gaming | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Small_Gaming.jpg?v=1783067837",
    "Living room | Gaming | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Medium_Gaming.jpg?v=1783064685",
    "Living room | Gaming | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Large_Gaming.jpg?v=1783064685",
    "Living room | Gaming | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_Gaming.jpg?v=1783064684",

    "Living room | Sports & live TV | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Small_Sports.jpg?v=1783067844",
    "Living room | Sports & live TV | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Medium_Sports.jpg?v=1783064713",
    "Living room | Sports & live TV | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Large_Sports.jpg?v=1783064713",
    "Living room | Sports & live TV | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_Sports.jpg?v=1783064713",

    "Living room | Everyday everything | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Small_Everyday.jpg?v=1783067850",
    "Living room | Everyday everything | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Medium_Everyday.jpg?v=1783064741",
    "Living room | Everyday everything | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Large_Everyday.jpg?v=1783064741",
    "Living room | Everyday everything | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_Everyday_7020ae44-f9f9-4f1c-af8e-43242e51f3c5.jpg?v=1783064741",

    "Bedroom | Movies & streaming | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Small_Movies.jpg?v=1783067607",
    "Bedroom | Movies & streaming | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_Movies.jpg?v=1783063138",
    "Bedroom | Movies & streaming | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_Movies.jpg?v=1783063138",
    "Bedroom | Movies & streaming | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_Movies.jpg?v=1783063138",

    "Bedroom | Gaming | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Small_Gaming.jpg?v=1783063171",
    "Bedroom | Gaming | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_Gaming.jpg?v=1783063171",
    "Bedroom | Gaming | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_Gaming.jpg?v=1783063171",
    "Bedroom | Gaming | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_Gaming.jpg?v=1783063171",

    "Bedroom | Sports & live TV | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Small_Sports.jpg?v=1783063200",
    "Bedroom | Sports & live TV | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_Sports.jpg?v=1783063200",
    "Bedroom | Sports & live TV | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_Sports.jpg?v=1783063199",
    "Bedroom | Sports & live TV | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_Sports.jpg?v=1783063200",

    "Bedroom | Everyday everything | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Small_Everyday.jpg?v=1783067600",
    "Bedroom | Everyday everything | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_Everyday.jpg?v=1783063231",
    "Bedroom | Everyday everything | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_Everyday.jpg?v=1783063230",
    "Bedroom | Everyday everything | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_Everyday.jpg?v=1783063231",

    "Entertainment room | Movies & streaming | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Small_Movies.jpg?v=1783063428",
    "Entertainment room | Movies & streaming | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Medium_Movies.jpg?v=1783063428",
    "Entertainment room | Movies & streaming | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Large_Movies.jpg?v=1783063429",
    "Entertainment room | Movies & streaming | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_SuperSized_Movies.jpg?v=1783063429",

    "Entertainment room | Gaming | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Small_Gaming.jpg?v=1783063465",
    "Entertainment room | Gaming | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Medium_Gaming.jpg?v=1783063465",
    "Entertainment room | Gaming | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Large_Gaming.jpg?v=1783063465",
    "Entertainment room | Gaming | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_SuperSized_Gaming.jpg?v=1783063465",

    "Entertainment room | Sports & live TV | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Small_Sports.jpg?v=1783063502",
    "Entertainment room | Sports & live TV | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Medium_Sports.jpg?v=1783063502",
    "Entertainment room | Sports & live TV | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Large_Sports.jpg?v=1783063502",
    "Entertainment room | Sports & live TV | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_SuperSized_Sports.jpg?v=1783063502",

    "Entertainment room | Everyday everything | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Small_Everyday.jpg?v=1783063549",
    "Entertainment room | Everyday everything | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Medium_Everyday.jpg?v=1783063549",
    "Entertainment room | Everyday everything | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Large_Everyday.jpg?v=1783067656",
    "Entertainment room | Everyday everything | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_Everyday.jpg?v=1783063969",

    "Study Room | Movies & streaming | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Small_Movies.jpg?v=1783064385",
    "Study Room | Movies & streaming | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Medium_Movies.jpg?v=1783064384",
    "Study Room | Movies & streaming | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Large_Movies.jpg?v=1783064384",
    "Study Room | Movies & streaming | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_SuperSized_Movies.jpg?v=1783064385",

    "Study Room | Gaming | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Small_Gaming.jpg?v=1783064411",
    "Study Room | Gaming | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Medium_Gaming.jpg?v=1783064411",
    "Study Room | Gaming | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Large_Gaming.jpg?v=1783064411",
    "Study Room | Gaming | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_SuperSized_Gaming.jpg?v=1783064411",

    "Study Room | Sports & live TV | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Small_Sports.jpg?v=1783064443",
    "Study Room | Sports & live TV | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Medium_Sports.jpg?v=1783064444",
    "Study Room | Sports & live TV | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Large_Sports.jpg?v=1783064444",
    "Study Room | Sports & live TV | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_SuperSized_Sports.jpg?v=1783064444",

    "Study Room | Everyday everything | Small 32–43″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Small_Everyday.jpg?v=1783064481",
    "Study Room | Everyday everything | Medium 55–65″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Medium_Everyday.jpg?v=1783064481",
    "Study Room | Everyday everything | Large 75–86″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Large_Everyday.jpg?v=1783064481",
    "Study Room | Everyday everything | SuperSized 100″":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_SuperSized_Everyday.jpg?v=1783064481",

    // ─────────── Step 4: Room | Size | Picture ───────────
    "Living room | Small 32–43″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Small_OLED.jpg?v=1783067893",
    "Living room | Medium 55–65″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Medium_OLED.jpg?v=1783064769",
    "Living room | Large 75–86″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Large_OLED.jpg?v=1783064768",
    "Living room | SuperSized 100″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_OLED.jpg?v=1783064769",

    "Living room | Small 32–43″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Small_MiniLED.jpg?v=1783067899",
    "Living room | Medium 55–65″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Medium_MiniLED.jpg?v=1783064792",
    "Living room | Large 75–86″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Large_MiniLED.jpg?v=1783064792",
    "Living room | SuperSized 100″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_MiniLED.jpg?v=1783064792",

    "Living room | Small 32–43″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Small_QLED.jpg?v=1783067905",
    "Living room | Medium 55–65″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Medium_QLED.jpg?v=1783064819",
    "Living room | Large 75–86″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Large_QLED.jpg?v=1783064819",
    "Living room | SuperSized 100″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_QLED.jpg?v=1783064819",

    "Living room | Small 32–43″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Small_Standard.jpg?v=1783067910",
    "Living room | Medium 55–65″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Medium_Standard.jpg?v=1783064844",
    "Living room | Large 75–86″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_Large_Standard.jpg?v=1783064844",
    "Living room | SuperSized 100″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/LivingRoom_SuperSized_Standard.jpg?v=1783064844",

    "Bedroom | Small 32–43″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Small_OLED.jpg?v=1783063262",
    "Bedroom | Medium 55–65″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_OLED.jpg?v=1783063262",
    "Bedroom | Large 75–86″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_OLED.jpg?v=1783063262",
    "Bedroom | SuperSized 100″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_OLED.jpg?v=1783063262",

    "Bedroom | Small 32–43″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Small_MiniLED.jpg?v=1783063291",
    "Bedroom | Medium 55–65″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_MiniLED.jpg?v=1783063291",
    "Bedroom | Large 75–86″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_MiniLED.jpg?v=1783063291",
    "Bedroom | SuperSized 100″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_MiniLED.jpg?v=1783063291",

    "Bedroom | Small 32–43″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Small_QLED.jpg?v=1783063323",
    "Bedroom | Medium 55–65″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_QLED.jpg?v=1783063323",
    "Bedroom | Large 75–86″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_QLED.jpg?v=1783063323",
    "Bedroom | SuperSized 100″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_QLED.jpg?v=1783063324",

    "Bedroom | Small 32–43″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Small_Standard.jpg?v=1783063358",
    "Bedroom | Medium 55–65″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Medium_Standard.jpg?v=1783063358",
    "Bedroom | Large 75–86″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_Large_Standard.jpg?v=1783063358",
    "Bedroom | SuperSized 100″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Bedroom_SuperSized_Standard.jpg?v=1783063357",

    "Entertainment room | Small 32–43″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Small_OLED.jpg?v=1783064002",
    "Entertainment room | Medium 55–65″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Medium_OLED.jpg?v=1783064002",
    "Entertainment room | Large 75–86″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Large_OLED.jpg?v=1783063999",
    "Entertainment room | SuperSized 100″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_SuperSized_OLED.jpg?v=1783064002",

    "Entertainment room | Small 32–43″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Small_MiniLED.jpg?v=1783064222",
    "Entertainment room | Medium 55–65″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Medium_MiniLED.jpg?v=1783064222",
    "Entertainment room | Large 75–86″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Large_MiniLED.jpg?v=1783064222",
    "Entertainment room | SuperSized 100″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_SuperSized_MiniLED.jpg?v=1783064222",

    "Entertainment room | Small 32–43″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Small_QLED.jpg?v=1783064252",
    "Entertainment room | Medium 55–65″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Medium_QLED.jpg?v=1783064252",
    "Entertainment room | Large 75–86″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Large_QLED.jpg?v=1783064252",
    "Entertainment room | SuperSized 100″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_SuperSized_QLED.jpg?v=1783064252",

    "Entertainment room | Small 32–43″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Small_Standard.jpg?v=1783064278",
    "Entertainment room | Medium 55–65″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Medium_Standard.jpg?v=1783064278",
    "Entertainment room | Large 75–86″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_Large_Standard.jpg?v=1783064278",
    "Entertainment room | SuperSized 100″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/EntertainmentRoom_SuperSized_Standard.jpg?v=1783064278",

    "Study Room | Small 32–43″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Small_OLED.jpg?v=1783064513",
    "Study Room | Medium 55–65″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Medium_OLED.jpg?v=1783064513",
    "Study Room | Large 75–86″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Large_OLED.jpg?v=1783064513",
    "Study Room | SuperSized 100″ | Infinite contrast (OLED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_SuperSized_OLED.jpg?v=1783064513",

    "Study Room | Small 32–43″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Small_MiniLED.jpg?v=1783064539",
    "Study Room | Medium 55–65″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Medium_MiniLED.jpg?v=1783064539",
    "Study Room | Large 75–86″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Large_MiniLED.jpg?v=1783064539",
    "Study Room | SuperSized 100″ | Extreme brightness (Mini-LED)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_SuperSized_MiniLED.jpg?v=1783064539",

    "Study Room | Small 32–43″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Small_QLED.jpg?v=1783064567",
    "Study Room | Medium 55–65″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Medium_QLED.jpg?v=1783064567",
    "Study Room | Large 75–86″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Large_QLED.jpg?v=1783064567",
    "Study Room | SuperSized 100″ | Wide colour gamut (QLED+)":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_SuperSized_QLED.jpg?v=1783064567",

    "Study Room | Small 32–43″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Small_Standard.jpg?v=1783064591",
    "Study Room | Medium 55–65″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Medium_Standard.jpg?v=1783064591",
    "Study Room | Large 75–86″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_Large_Standard.jpg?v=1783064591",
    "Study Room | SuperSized 100″ | Good standard picture":
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/HomeOffice_SuperSized_Standard.jpg?v=1783064592",
  };
  const ICONS = {
    livingRoom:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/living-room.svg?v=1780628949",
    bedroom:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/bedroom.svg?v=1780628949",
    entertainmentRoom:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/entertainment-room.svg?v=1780628949",
    study:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/study-home-office.svg?v=1780628949",
    movies:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Movies.svg?v=1780629021",
    gaming:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Gaming.svg?v=1780629020",
    sports:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Sports.svg?v=1780629021",
    tv: "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/tv.svg?v=1780629035",
    small:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Small.svg?v=1780629075",
    medium:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Medium.svg?v=1780629075",
    large:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Large.svg?v=1780629075",
    superSize:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Super.svg?v=1780629075",
    oled: "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/OLED.svg?v=1780629266",
    brightness:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/brightness.svg?v=1780629266",
    colourGamut:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/Colour_Gamut.svg?v=1780629266",
    standard4k:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/standard-4k-picture.svg?v=1780629265",
    hz144:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/144hz.svg?v=1780630000",
    hdmi: "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/hdmi.svg?v=1780630000",
    dimmingZones:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/dimmingzones.svg?v=1780630048",
    dolbyVisionIq:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/dolby-vision-iq.svg?v=1780630111",
    dolbyAtmos:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/dolby-atmos-audio.svg?v=1780630111",
    dts: "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/dts.svg?v=1780630111",
    airplay:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/airplay.svg?v=1780630110",
    aiProcessor:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/ai_processor.svg?v=1780630111",
    eyeCare:
      "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/eye-care-certified.svg?v=1780630111",
    memc: "https://cdn.shopify.com/s/files/1/0703/1498/3651/files/memc.svg?v=1780630111",
  };
  const QUESTIONS = [
    {
      title: "Where will you put the TV?",
      subtitle:
        "This helps us match the right size and brightness for your space.",
      answers: [
        {
          title: "Living room",
          description: "Main household screen, family viewing",
          icon: ICONS.livingRoom,
        },
        {
          title: "Bedroom",
          description: "Closer viewing distance, cosy setup",
          icon: ICONS.bedroom,
        },
        {
          title: "Entertainment room",
          description: "Dedicated home cinema or games room",
          icon: ICONS.entertainmentRoom,
        },
        {
          title: "Study Room",
          description: "Smaller space, casual background TV",
          icon: ICONS.study,
        },
      ],
    },
    {
      title: "What screen size are you looking for?",
      subtitle: "Already narrowed by your room, here are the best fit for you.",
      answers: [
        {
          title: "Small 32–43″",
          description: "Bedroom, kitchen or compact spaces",
          icon: ICONS.small,
        },
        {
          title: "Medium 55–65″",
          description: "Standard living or bedroom setup",
          icon: ICONS.medium,
        },
        {
          title: "Large 75–86″",
          description: "Spacious lounge or entertainment room",
          icon: ICONS.large,
        },
        {
          title: "SuperSized 100″",
          description: "Home cinema, statement screen",
          icon: ICONS.superSize,
        },
      ],
    },
    {
      title: "What will the TV mainly be used for?",
      subtitle: "Pick the one that fits your household best.",
      answers: [
        {
          title: "Movies & streaming",
          description: "Netflix, Disney+, Apple TV+, cinematic feel",
          icon: ICONS.movies,
        },
        {
          title: "Gaming",
          description: "Console, PC, low input lag, high refresh rate",
          icon: ICONS.gaming,
        },
        {
          title: "Sports & live TV",
          description: "Fast motion, bright sports content",
          icon: ICONS.sports,
        },
        {
          title: "Everyday everything",
          description: "News, casual streaming, a bit of everything",
          icon: ICONS.tv,
        },
      ],
    },
    {
      title: "What kind of picture quality matters most?",
      subtitle: "Some options may not be available in your chosen screen size.",
      answers: [
        {
          title: "Infinite contrast (OLED)",
          description: "Self-lit pixels — perfect blacks, stunning depth",
          icon: ICONS.oled,
        },
        {
          title: "Extreme brightness (Mini-LED)",
          description: "Up to 2320 nits, 1800 zones — ideal for bright rooms",
          icon: ICONS.brightness,
        },
        {
          title: "Wide colour gamut (QLED+)",
          description: "118% DCI-P3 — vivid and accurate colour",
          icon: ICONS.colourGamut,
        },
        {
          title: "Good standard picture",
          description: "Solid 4K image at the best price",
          icon: ICONS.standard4k,
        },
      ],
    },
    {
      title: "Pick the features you want",
      subtitle:
        "Select all that matter, <span class='red-text'>*multiple selection</span>",
      multi: true,
      answers: [
        {
          title: "144Hz VRR",
          description: "Eliminates screen tearing for gaming",
          icon: ICONS.hz144,
        },
        {
          title: "HDMI 2.1 (×3 ports)",
          description: "4K@120Hz for PS5 / Xbox Series X",
          icon: ICONS.hdmi,
        },
        {
          title: "OLED panel",
          description: "Perfect blacks, infinite contrast, ultra-thin",
          icon: ICONS.oled,
        },
        {
          title: "Mini-LED local dimming zones",
          description: "Up to 1800 zones for precise backlight control",
          icon: ICONS.dimmingZones,
        },
        {
          title: "Filmmaker Mode",
          description: "Watch film as director intended — no processing",
          icon: ICONS.movies,
        },
        {
          title: "Dolby Vision IQ",
          description: "Dynamic HDR adapts to room lighting",
          icon: ICONS.dolbyVisionIq,
        },
        {
          title: "Dolby Atmos audio",
          description: "Immersive 3D spatial sound",
          icon: ICONS.dolbyAtmos,
        },
        {
          title: "DTS TruSurround",
          description: "Virtualised surround sound",
          icon: ICONS.dts,
        },
        {
          title: "AirPlay 2",
          description: "Stream from iPhone, iPad or Mac",
          icon: ICONS.airplay,
        },
        {
          title: "AI Picture Processor",
          description: "Upscales and enhances image automatically",
          icon: ICONS.aiProcessor,
        },
        {
          title: "Eye Care certified",
          description: "Low blue light, flicker-free",
          icon: ICONS.eyeCare,
        },
        {
          title: "MEMC motion smoothing",
          description: "Reduces motion blur for sports and action",
          icon: ICONS.memc,
        },
      ],
    },
  ];
  const SELECTED = {
    0: [],
    1: [],
    2: [],
    3: [],
    4: [],
  };
  // Some product pages pull their JudgeMe reviews from a *different* product.
  // Mirror that here so the results panel shows the same rating: map the main
  // product handle → the handle whose reviews should be shown instead.
  // Only the rating/review-count are borrowed; price & voucher stay from main.
  const REVIEW_SOURCE_HANDLES = {
    "q43qe-2026": "q43qe-a-q43qe-google-tv",
    "q55qe-2026": "q55qe-a-q55qe-google-tv",
    "q65qe-2026": "q65qe-a-q55qe-google-tv",
    "q75qe-2026": "q75qe-a-q55qe-google-tv",
    "u55-2026": "u55-qled-mini-led-google",
    "u65-2026": "u65-qled-mini-led-google",
    "u75-2026": "u75-qled-mini-led-google",
  };
  const TVS = [
    // Template
    // {
    //   series: "",
    //   list: [
    //     {
    //       title: "",
    //       handle: "",
    //       room: [],
    //       use: [],
    //       size: [],
    //       picture: [],
    //       features: [],
    //     },
    //   ],
    // },
    {
      series: "AL-Series",
      list: [
        {
          title: "55AL",
          inches: 55,
          handle: "prism-55al-google-tv",
          room: [TYPES.room.living_room, TYPES.room.bedroom],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.medium],
          picture: [TYPES.picture.oled],
          features: [
            TYPES.features.hdmi,
            TYPES.features.oled_panel,
            TYPES.features.filmmaker_mode,
            TYPES.features.dolby_vision,
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
            TYPES.features.ai_picture,
          ],
        },
        {
          title: "65AL",
          inches: 65,
          handle: "prism-65al-google-tv",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.medium],
          picture: [TYPES.picture.oled],
          features: [
            TYPES.features.hdmi,
            TYPES.features.oled_panel,
            TYPES.features.filmmaker_mode,
            TYPES.features.dolby_vision,
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
            TYPES.features.ai_picture,
          ],
        },
      ],
    },
    {
      series: "U-Ultra Series",
      list: [
        {
          title: "U65 Ultra",
          inches: 65,
          handle: "prism-u65-ultra-qd-mini-led-4k-ai-google-tv",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.medium],
          picture: [TYPES.picture.mini_led],
          features: [
            TYPES.features["144Hz"],
            TYPES.features.hdmi,
            TYPES.features.miniled_local_dimming,
            TYPES.features.filmmaker_mode,
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "U75 Ultra",
          inches: 75,
          handle: "prism-u75-ultra-qd-mini-led-4k-ai-google-tv",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.large],
          picture: [TYPES.picture.mini_led],
          features: [
            TYPES.features["144Hz"],
            TYPES.features.hdmi,
            TYPES.features.miniled_local_dimming,
            TYPES.features.filmmaker_mode,
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "U86 Ultra",
          inches: 86,
          handle: "prism-u86-ultra-qd-mini-led-4k-ai-google-tv",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.large],
          picture: [TYPES.picture.mini_led],
          features: [
            TYPES.features["144Hz"],
            TYPES.features.hdmi,
            TYPES.features.miniled_local_dimming,
            TYPES.features.filmmaker_mode,
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
      ],
    },
    {
      series: "U-Series",
      list: [
        {
          title: "U75",
          inches: 75,
          handle: "u75-qled-mini-led-google",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.large],
          picture: [TYPES.picture.mini_led],
          features: [
            TYPES.features["144Hz"],
            TYPES.features.hdmi,
            TYPES.features.miniled_local_dimming,
            TYPES.features.filmmaker_mode,
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
            TYPES.features.ai_picture,
          ],
        },
        {
          title: "U86",
          inches: 86,
          handle: "prism-u86-qd-mini-led",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.large],
          picture: [TYPES.picture.mini_led],
          features: [
            TYPES.features["144Hz"],
            TYPES.features.hdmi,
            TYPES.features.miniled_local_dimming,
            TYPES.features.filmmaker_mode,
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
            TYPES.features.ai_picture,
          ],
        },
      ],
    },
    {
      series: "U-Series 2026",
      list: [
        {
          title: "U55 2026",
          inches: 55,
          handle: "u55-2026",
          room: [TYPES.room.living_room, TYPES.room.bedroom],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.medium],
          picture: [TYPES.picture.mini_led],
          features: [
            TYPES.features.hdmi,
            TYPES.features.miniled_local_dimming,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "U65 2026",
          inches: 65,
          handle: "u65-2026",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.medium],
          picture: [TYPES.picture.mini_led],
          features: [
            TYPES.features.hdmi,
            TYPES.features.miniled_local_dimming,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "U75 2026",
          inches: 75,
          handle: "u75-2026",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.large],
          picture: [TYPES.picture.mini_led],
          features: [
            TYPES.features.hdmi,
            TYPES.features.miniled_local_dimming,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
      ],
    },
    {
      series: "Q-Ultra Series 2026",
      list: [
        {
          title: "Q43 Ultra 2026",
          inches: 43,
          handle: "q43u-2026",
          room: [TYPES.room.bedroom, TYPES.room.study],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.small],
          picture: [TYPES.picture.qled],
          features: [
            TYPES.features.hdmi,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "Q55 Ultra 2026",
          inches: 55,
          handle: "q55u-2026",
          room: [TYPES.room.living_room, TYPES.room.bedroom],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.medium],
          picture: [TYPES.picture.qled],
          features: [
            TYPES.features.hdmi,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "Q65 Ultra 2026",
          inches: 65,
          handle: "q65u-2026",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.medium],
          picture: [TYPES.picture.qled],
          features: [
            TYPES.features.hdmi,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "Q75 Ultra 2026",
          inches: 75,
          handle: "q75u-2026",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.large],
          picture: [TYPES.picture.qled],
          features: [
            TYPES.features.hdmi,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "Q85 Ultra 2026",
          inches: 85,
          handle: "q85u-2026",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.large],
          picture: [TYPES.picture.qled],
          features: [
            TYPES.features.hdmi,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
      ],
    },
    {
      series: "Q-Ultra Series",
      list: [
        {
          title: "Q100 Ultra",
          inches: 100,
          handle: "prism-q100-ultra-qled-google-tv",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [
            TYPES.use.gaming,
            TYPES.use.movies,
            TYPES.use.sports,
            TYPES.use.everyday,
          ],
          size: [TYPES.size.xlarge],
          picture: [TYPES.picture.qled],
          features: [
            TYPES.features.hdmi,
            TYPES.features.filmmaker_mode,
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
      ],
    },
    {
      series: "Q-QE Series 2026",
      list: [
        {
          title: "Q43-QE 2026",
          inches: 43,
          handle: "q43qe-2026",
          room: [TYPES.room.bedroom, TYPES.room.study],
          use: [TYPES.use.gaming, TYPES.use.movies, TYPES.use.everyday],
          size: [TYPES.size.small],
          picture: [TYPES.picture.standard],
          features: [
            TYPES.features.hdmi,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "Q55-QE 2026",
          inches: 55,
          handle: "q55qe-2026",
          room: [TYPES.room.living_room, TYPES.room.bedroom],
          use: [TYPES.use.gaming, TYPES.use.movies, TYPES.use.everyday],
          size: [TYPES.size.medium],
          picture: [TYPES.picture.standard],
          features: [
            TYPES.features.hdmi,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "Q65-QE 2026",
          inches: 65,
          handle: "q65qe-2026",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [TYPES.use.gaming, TYPES.use.movies, TYPES.use.everyday],
          size: [TYPES.size.medium],
          picture: [TYPES.picture.standard],
          features: [
            TYPES.features.hdmi,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
        {
          title: "Q75-QE 2026",
          inches: 75,
          handle: "q75qe-2026",
          room: [TYPES.room.living_room, TYPES.room.entertainment_room],
          use: [TYPES.use.gaming, TYPES.use.movies, TYPES.use.everyday],
          size: [TYPES.size.large],
          picture: [TYPES.picture.standard],
          features: [
            TYPES.features.hdmi,
            TYPES.features.dolby_atmos,
            TYPES.features.memc,
            TYPES.features.airplay,
            TYPES.features.ai_picture,
            TYPES.features.eye_care,
          ],
        },
      ],
    },
    {
      series: "S-Series",
      list: [
        {
          title: "S32",
          inches: 32,
          handle: "s32-google-tv",
          room: [TYPES.room.bedroom, TYPES.room.study],
          use: [TYPES.use.everyday],
          size: [TYPES.size.small],
          picture: [TYPES.picture.standard],
          features: [TYPES.features.dolby_atmos, TYPES.features.memc],
        },
        {
          title: "S40",
          inches: 40,
          handle: "prism-s40-google-tv",
          room: [TYPES.room.bedroom, TYPES.room.study],
          use: [TYPES.use.everyday],
          size: [TYPES.size.small],
          picture: [TYPES.picture.standard],
          features: [
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
          ],
        },
        {
          title: "S43",
          inches: 43,
          handle: "prism-s43-google-tv",
          room: [TYPES.room.bedroom, TYPES.room.study],
          use: [TYPES.use.everyday],
          size: [TYPES.size.small],
          picture: [TYPES.picture.standard],
          features: [
            TYPES.features.dolby_atmos,
            TYPES.features.dts_trusurround,
            TYPES.features.memc,
          ],
        },
      ],
    },
  ];

  // ── Cascade constraints, derived live from the TVS catalog ──
  // This reproduces tv_quiz_cascade_constraints.json without duplicating it:
  // every eligible/disabled rule in that file is just "does at least one TV
  // survive this choice?". Room (step 0) is context only and never filters;
  // size/use/picture are single-select (exact match); features are
  // multi-select (match on ANY ticked feature).
  // NOTE: defined here (above main()) so the const bindings are initialized
  // before any handler wired up during init can reach them.
  const STEP_PROP = { 1: "size", 2: "use", 3: "picture", 4: "features" };
  const ALL_TVS = TVS.flatMap((series) => series.list);

  function tvSatisfies(tv, selections) {
    return Object.keys(STEP_PROP).every((step) => {
      const sel = selections[step] || [];
      if (sel.length === 0) return true; // step not answered → no filter
      const tvValues = (tv[STEP_PROP[step]] || []).map((v) => v.toLowerCase());
      return sel.some((s) => tvValues.includes(s.toLowerCase()));
    });
  }

  // Would picking `title` at `step` still leave at least one reachable TV,
  // given everything chosen in the earlier steps? That is exactly the
  // eligible/disabled rule the constraints file encodes.
  function isEligible(step, title) {
    if (!STEP_PROP[step]) return true; // room — never filtered
    const hypo = {};
    Object.keys(STEP_PROP).forEach((s) => {
      if (Number(s) < step) hypo[s] = SELECTED[s];
    });
    hypo[step] = [title];
    return ALL_TVS.some((tv) => tvSatisfies(tv, hypo));
  }

  function disabledNote(step) {
    const size = (SELECTED[1] && SELECTED[1][0]) || "this size";
    const picture = (SELECTED[3] && SELECTED[3][0]) || "this picture";
    if (step === 4) return `Not available with ${picture}`;
    return `Not available at ${size}`;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", main);
  } else {
    main();
  }

  function main() {
    preloadImages();
    addStyle();
    addButton();
    addPopup();
    addHorizontalScroll();
    addEvents();
  }

  function preloadImages() {
    const urls = new Set([
      PLACEHOLDER_IMAGE,
      PLACEHOLDER_ICON,
      ...Object.values(IMAGES).filter(Boolean),
      ...Object.values(ICONS).filter(Boolean),
    ]);

    urls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }

  function addStyle() {
    const style = document.createElement("style");
    style.textContent = `
      .help-me-choose-button {
        width: 100%;
        margin-top: 1rem;
        border: 2px solid #7a0000;
        background: #fff;
        padding: 12px;
        color: #7a0000;
        font-weight: 700;
        text-align: center;
      }
      @media (min-width: 1024px) {
        .help-me-choose-button {
          position: absolute;
          top: 0;
          right: 0;
          max-width: 180px;
          margin-top: 0px;
        }
        .tv_compare_btn {
          right: 200px;
        }
      }
      
      body:has(.help-me-choose-overlay:not(.hide)) {
        overflow: hidden;
      }
      .help-me-choose-overlay {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 1rem;
        z-index: 9999;
      }
      .help-me-choose-popup {
        position: relative;
        display: flex;
        flex-direction: column;
        background: #fff;
        padding: 20px;
        border-radius: 8px;
        max-width: 1200px;
        max-height: calc(100vh - 2rem);
        max-height: calc(100dvh - 2rem); /* dvh tracks the live mobile viewport */
        width: 100%;
        overflow: hidden; /* the body region scrolls, not the whole popup */
        box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
      }
      .help-me-choose-popup p {
        margin: 0;
      }
      .help-me-choose-close-button {
        position: absolute;
        top: 10px;
        right: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 4px;
        background: #fff;
        color: #888888;
        border: 1px solid #EEEEEE;
        border-radius: 50%;
        outline: none;
        transition: all 0.2s;
      }
      .help-me-choose-close-button:hover {
        color: #000000;
        border-color: #000000;
        box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
      }
      .help-me-choose-close-button svg {
        width: 1rem;
        height: 1rem;
        color: currentColor;
      }
      @media (min-width: 1024px) {
        .help-me-choose-close-button svg {
          width: 20px;
          height: 20px;
        }
      }

      .help-me-choose-content {
        position: relative;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0; /* let the body child shrink so it can scroll */
        gap: 1rem;
        margin-top: 1.5rem;
      }
      @media (min-width: 1024px) {
        .help-me-choose-content {
          margin-top: 2rem;
        }
      }

      /* The only scrolling region: sits between the fixed header and footer.
         overflow-x must be hidden explicitly — with overflow-y:auto the browser
         would otherwise compute overflow-x to auto, letting the content scroll
         horizontally (blank space + rubber-band bounce on mobile). */
      .help-me-choose-body {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        gap: 1rem;
        overflow-x: hidden;
        overflow-y: auto;
      }
      /* Keep each section at its natural height so the body scrolls instead of
         flexbox shrinking them — otherwise the feature strip (a scroll
         container) gets squished and clips its cards vertically on short
         screens. */
      .help-me-choose-body > * {
        flex-shrink: 0;
      }

      .help-me-choose-header {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .help-me-choose-header p {
        color: #999999;
        font-size: 12px;
        font-weight: 600;
      }
      .help-me-choose-header h2 {
        margin: 0;
        color: #000000;
        font-size: 20px;
        font-weight: 600;
        text-align: left;
      }

      .help-me-choose-question {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .help-me-choose-question h2 {
        margin: 0;
        color: #000000;
        font-size: 20px;
        font-weight: 600;
        text-align: left;
      }
      .help-me-choose-question p {
        margin: 0;
        color: #999999;
        font-size: 12px;
        font-weight: 600;
      }

      .img-main {
        width: 100%;
        height: auto;
        margin: 0 auto;
        object-fit: contain;
        transition: opacity 0.25s ease;
      }
      .img-main.is-swapping {
        opacity: 0;
      }
      @media (min-width: 1024px) {
        .img-main {
          width: 85%;
          height: 350px;
        }
      }

      .help-me-choose-progress {
        --bar-color: #7A0001;
        --background-color: #D9D9D9;
        --border-radius: 5px;

        width: 100%;
        height: 10px;
        color: var(--bar-color);
        background: var(--background-color);
        border-radius: var(--border-radius);
        overflow: hidden;
      }
      .help-me-choose-progress::-webkit-progress-bar {
        background: var(--background-color);
        border-radius: var(--border-radius);
      }
      .help-me-choose-progress::-webkit-progress-value {
        background: var(--bar-color);
        transition: width 1s;
      }

      .help-me-choose-selected {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 4px;
        overflow-x: auto;
        white-space: nowrap;
      }
      .help-me-choose-selected p {
        display: flex;
        align-items: center;
        gap: 0px;
        font-size: 12px;
        font-weight: 600;
      }
      .help-me-choose-selected svg {
        width: 1rem;
        height: 1rem;
      }
      .help-me-choose-selected-list {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 4px;
        overflow-x: auto;
      }
      .help-me-choose-selected-item {
        padding: 4px 8px;
        background: #000000CC;
        color: #ffffff;
        border-radius: 50px;
        font-size: 12px;
        font-weight: 600;
      }
      @media (min-width: 1024px) {
        .help-me-choose-selected {
          gap: 8px;
        }
        .help-me-choose-selected p {
          gap: 4px;
        }
        .help-me-choose-selected-list {
          gap: 6px;
        }
      }

      .help-me-choose-selection {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
      }
      .help-me-choose-selection[data-multi="true"] {
        display: flex;
        overflow-x: auto;
        user-select: none;
        cursor: grab;
      }
      .help-me-choose-selection[data-multi="true"]:active {
        cursor: grabbing;
      }
      .help-me-choose-selection[data-multi="true"]::-webkit-scrollbar {
        /* display: none; */
      }
      .help-me-choose-selection .option {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 1rem;
        border: 1px solid #000000CC;
        cursor: pointer;
        transition: all 0.2s;
        min-width: 0; /* let the grid/flex cell shrink so text wraps instead of overflowing */
        white-space: normal; /* don't inherit white-space:nowrap from the theme/page */
      }
      .help-me-choose-selection[data-multi="true"] .option {
        min-width: calc(100% / 2);
      }
      .help-me-choose-selection[data-multi="true"].is-dragging .option {
        cursor: grabbing;
      }
      .help-me-choose-selection .option:hover {
        box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
      }
      .help-me-choose-selection .option.selected {
        background: #000000CC;
      }
      .help-me-choose-selection .option-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 55px;
        height: 42px;
        padding: 10px;
        border: 1px solid #00000026;
        background: #00000014;
      }
      .help-me-choose-selection .option-icon img {
        width: 100%;
        height: auto;
      }
      .help-me-choose-selection .option-title {
        color: #000000;
        font-weight: 600;
        overflow-wrap: break-word; /* break long tokens rather than overflow */
      }
      .help-me-choose-selection .option-desc {
        color: #000000B2;
        font-size: 12px;
        overflow-wrap: break-word;
      }
      .help-me-choose-selection .option.selected .option-icon {
        border: 1px solid #FFFFFF26;
        background: #FFFFFF14;
      }
      .help-me-choose-selection .option.selected .option-icon img {
        filter: brightness(0) invert(1);
      }
      .help-me-choose-selection .option.selected .option-title {
        color: #ffffff;
      }
      .help-me-choose-selection .option.selected .option-desc {
        color: #FFFFFFB2;
      }
      .help-me-choose-selection .option.disabled {
        cursor: not-allowed;
        opacity: 0.4;
        pointer-events: none;
      }
      .help-me-choose-selection .option.disabled:hover {
        box-shadow: none;
      }
      .help-me-choose-selection .option.disabled .option-desc {
        color: #B00020;
        font-style: italic;
      }
      @media (min-width: 768px) {
        .help-me-choose-selection[data-multi="true"] .option {
          min-width: calc(100% / 3);
        }
      }
      @media (min-width: 1024px) {
        .help-me-choose-selection {
          grid-template-columns: repeat(4, 1fr);
        }
        .help-me-choose-selection[data-multi="true"] .option {
          min-width: calc(100% / 4);
        }
      }

      .help-me-choose-nav {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding-top: 1rem;
        border-top: 1px solid #0000001a; /* divide the fixed footer from the scroll area */
      }
      .help-me-choose-nav button {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 4px;
        padding: 12px;
        border-radius: 4px;
        font-weight: 600;
        white-space: nowrap;
      }
      .help-me-choose-nav button svg {
        width: 1rem;
        height: 1rem;
      }
      .help-me-choose-nav button.invisible {
        visibility: hidden;
      }
      .help-me-choose-nav .btn-prev,
      .help-me-choose-nav .btn-reset {
        background: #ffffff;
        color: #7A0000;
        border: 1px solid #7A0000;
      }
      .help-me-choose-nav .btn-next {
        background: #7A0001;
        color: #ffffff;
        border: 1px solid #7A0001;
      }

      @media (max-width: 1023px) {
        .help-me-choose-content[data-step="final"] .help-me-choose-selected {
          flex-wrap: wrap;
        }
        .help-me-choose-content[data-step="final"] .help-me-choose-nav {
          flex-wrap: wrap;
        }
      }

      .help-me-choose-results {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
      }
      .help-me-choose-results .tv-list {
        display: flex;
        flex-wrap: nowrap;
        gap: 1rem;
        overflow-x: auto;
        min-width: 0;
      }
      .help-me-choose-results .tv-list .tv-item {
        display: grid;
        grid-template-columns: 60px auto;
        flex: 0 0 auto;
        align-items: center;
        gap: 4px;
        border: 1px solid #000;
        border-radius: 8px;
        overflow: hidden;
        cursor: pointer;
        padding: 8px 10px;
      }
      .help-me-choose-results .tv-list .tv-item .column {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .help-me-choose-results .tv-list .tv-item .col-left svg {
        width: 20px;
        height: 20px;
        border: 1px solid #EBEBEB;
        border-radius: 50%;
        color: transparent;
      }
      .help-me-choose-results .tv-list .tv-item.selected {
        background-color: #6F000A0D;
        border-left-width: 3px;
        border-style: solid;
        border-color: #7A0001;
      }
      .help-me-choose-results .tv-list .tv-item.selected .col-left svg {
        color: #7a0000;
        border: none;
      }
      .help-me-choose-results .tv-list .tv-item .col-left img {
        max-width: 100%;
        height: auto;
      }
      .help-me-choose-results .tv-list .tv-item .col-right {
        align-items: flex-start;
      }

      .help-me-choose-results .tv-media {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
      }
      .help-me-choose-results .tv-media .image-list {
        display: flex;
        align-items: center;
      }
      .help-me-choose-results .tv-media .image-list .image-item {
        display: flex;
        max-width: 60px;
        border: 1px solid transparent;
        cursor: pointer;
      }
      .help-me-choose-results .tv-media .image-item:hover {
        border-color: #999999;
      }
      .help-me-choose-results .tv-media .image-item:has(.hideme) {
        display: none;
      }
      .help-me-choose-results .tv-media .image-item.active {
        border-color: #7a0001;
      }

      .help-me-choose-results .tv-info {
        padding: 1rem;
      }
      .help-me-choose-results .tv-info .tv-subtitle {
        margin-top: 2px;
      }
      .help-me-choose-results .tv-info .tv-model {
        margin-top: 0;
      }
      .help-me-choose-results .tv-info .tv-meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        margin: 10px 0;
      }
      .help-me-choose-results .tv-info .tv-rating {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: #666;
      }
      .help-me-choose-results .tv-info .tv-star {
        color: #d9d9d9;
        font-size: 14px;
        line-height: 1;
      }
      .help-me-choose-results .tv-info .tv-star.filled {
        color: #f5a623;
      }
      .help-me-choose-results .tv-info .tv-stock {
        display: inline-flex;
        align-items: center;
      }
      .help-me-choose-results .tv-info .tv-stock::before {
        content: "";
        width: 7px;
        height: 7px;
        border-radius: 50%;
        margin-right: 5px;
        background-color: currentColor;
      }
      .help-me-choose-results .tv-info .tv-stock.in-stock {
        color: #1a8a2e;
      }
      .help-me-choose-results .tv-info .tv-stock.out-stock {
        color: #999999;
      }
      .help-me-choose-results .tv-info .tv-pricing {
        margin: 12px 0;
      }
      .help-me-choose-results .tv-info .tv-price-regular {
        margin-right: 8px;
      }
      .help-me-choose-results .tv-info .tv-price-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        border-radius: 4px;
        background-color: #7a0000;
        color: #ffffff;
        font-weight: 700;
        font-size: 14px;
      }
      .help-me-choose-results .tv-info .tv-price-compare {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 6px;
        font-size: 13px;
      }
      .help-me-choose-results .tv-info .tv-installments {
        margin-top: 6px;
      }
      .help-me-choose-results .tv-info .tv-installments img {
        width: auto;
        height: 14px;
        margin: 0 4px;
      }
      .help-me-choose-results .tv-info .tv-features {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        margin-top: 16px;
      }
      .help-me-choose-results .tv-info .tv-feature {
        display: flex;
        flex-direction: column;
        gap: 3px;
        padding: 10px;
        border: 1px solid #0000001a;
        border-radius: 6px;
      }
      .help-me-choose-results .tv-info .tv-feature-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        padding: 2px;
        margin-bottom: 2px;
        border: 1px solid #00000026;
        background: #00000014;
      }
      .help-me-choose-results .tv-info .tv-feature-icon img {
        width: 100%;
        height: auto;
      }
      .help-me-choose-results .tv-info .tv-feature-title {
        line-height: 1.2;
      }
      .help-me-choose-results .tv-info .tv-feature-desc {
        line-height: 1.3;
      }
      .help-me-choose-results .tv-info .tv-actions {
        margin-top: 16px;
      }
      .help-me-choose-results .tv-info .tv-actions .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 12px;
        border-radius: 4px;
      }
      .help-me-choose-results .tv-info .tv-voucher-icon {
        transform: rotate(90deg);
      }

      .help-me-choose-results .tv-loading {
        grid-column: 1 / -1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        min-height: 300px;
        padding: 2rem;
      }
      .help-me-choose-results .tv-loading-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #EBEBEB;
        border-top-color: #7a0000;
        border-radius: 50%;
        animation: help-me-choose-spin 0.8s linear infinite;
      }
      @keyframes help-me-choose-spin {
        to { transform: rotate(360deg); }
      }

      .help-me-choose-results .tv-empty {
        grid-column: 1 / -1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 0.5rem;
        min-height: 300px;
        padding: 2rem;
      }

      @media (min-width: 1024px) {
        .help-me-choose-results {
          grid-template-columns: 1fr 2fr 1fr;
          gap: 0px;
        }
        .help-me-choose-results .tv-list {
          flex-direction: column;
          gap: 0px;
        }
        .help-me-choose-results .tv-list .tv-item {
          grid-template-columns: 100px auto;
          border-radius: 0px;
          border-color: #0000001A;
        }
        .help-me-choose-results .tv-list .tv-item.selected {
          border-right-width: 0px;
          border-top-color: #0000001A;
          border-bottom-color: #0000001A;
        }
        .help-me-choose-results .tv-list .tv-item .col-left {
          flex-direction: row;
        }
        .help-me-choose-results .tv-list .tv-item .col-left img {
          flex: 1;
          min-width: 0;
        }
        .help-me-choose-results .tv-media {
          background-color: #F8F8F8;
        }
        .help-me-choose-results .tv-media img {
          mix-blend-mode: multiply;
        }
        .help-me-choose-results .tv-media .image-list {
          gap: 1rem;
        }
      }

      .bold {
        font-weight: 700;
      }
      .semibold {
        font-weight: 600;
      }
      .subdued {
        color: #999999;
      }
      .text-red {
        color: #7a0001;
      }
      .remove-bg {
        mix-blend-mode: multiply;
      }
      .d-flex {
        display: flex;
      }
      .flex-col {
        flex-direction: column;
      }
      .help-me-choose-popup .btn {
        background-color: #fff;
        border: 1px solid #7a0000;
        color: #7a0000;
        text-align: center;
      }
      .help-me-choose-popup .btn.btn-primary {
        background-color: #7a0000;
        color: #ffffff;
      }
    `;

    for (let px = 8; px <= 20; px++) {
      style.textContent += `.fs-${px}{font-size:${px}px;}`;
    }

    for (let px = 1; px <= 16; px++) {
      style.textContent += `.gap-${px}{gap:${px}px;}`;
    }

    document.head.appendChild(style);
  }

  function addButton() {
    const button = document.createElement("button");
    button.textContent = "Help Me Choose";
    button.type = "button";
    button.classList.add("help-me-choose-button");
    button.addEventListener("click", function () {
      togglePopup();
    });

    $(button).insertAfter(".tv_compare_btn");
  }

  function togglePopup() {
    const overlay = $(".help-me-choose-overlay");
    overlay.toggleClass("hide");
    if (overlay.hasClass("hide")) {
      resetSelected();
      resetPopupContent();
    }
  }

  function addPopup() {
    const overlay = document.createElement("div");
    overlay.classList.add("help-me-choose-overlay", "hide");

    // Close on backdrop click — but only when the press STARTED on the
    // backdrop too. Otherwise a drag that begins inside the popup (e.g.
    // scrolling the step-5 feature strip) and releases on the overlay fires
    // a click on the overlay and would wrongly close the popup.
    let pressStartedOnOverlay = false;
    overlay.addEventListener("mousedown", (e) => {
      pressStartedOnOverlay = e.target === overlay;
    });
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay && pressStartedOnOverlay) {
        togglePopup();
      }
    });

    const popup = document.createElement("div");
    popup.classList.add("help-me-choose-popup");
    popup.innerHTML = `<div class="help-me-choose-content"></div>`;

    const closeButton = document.createElement("button");
    closeButton.classList.add("help-me-choose-close-button");
    closeButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>`;
    closeButton.type = "button";
    closeButton.addEventListener("click", togglePopup);

    document.body.append(overlay);
    overlay.append(popup);
    popup.prepend(closeButton);

    modifyPopupContent(0);
  }

  //   Step 0 (Room):    "Room"
  //   Step 1 (Size):    "Room | Size"
  //   Step 2 (Use):     "Room | Use | Size"
  //   Step 3 (Picture): "Room | Size | Picture"   (use is intentionally dropped)
  //   Step 4 (Features): reuses the Step 3 image for the picture the user chose
  function imageFor(step, ans) {
    let key = null;
    switch (step) {
      case 0:
        key = ans.title;
        break;
      case 1:
        key = `${SELECTED[0][0]} | ${ans.title}`;
        break;
      case 2:
        // Image keys are written "Room | Use | Size"; size lives in SELECTED[1].
        key = `${SELECTED[0][0]} | ${ans.title} | ${SELECTED[1][0]}`;
        break;
      case 3:
        key = `${SELECTED[0][0]} | ${SELECTED[1][0]} | ${ans.title}`;
        break;
      case 4:
        // Features step shows no image of its own — carry over the picture
        // image the user landed on (room | size | chosen picture).
        key = `${SELECTED[0][0]} | ${SELECTED[1][0]} | ${SELECTED[3][0]}`;
        break;
    }
    return (key && IMAGES[key]) || PLACEHOLDER_IMAGE;
  }

  let imageSwapTimer = null;
  function setMainImage(src) {
    const img = document.querySelector(".help-me-choose-content .img-main");
    if (!img || img.getAttribute("src") === src) return;

    // First paint has nothing to fade from — just show it.
    if (!img.getAttribute("src")) {
      img.setAttribute("src", src);
      return;
    }

    clearTimeout(imageSwapTimer);
    img.classList.add("is-swapping");
    imageSwapTimer = setTimeout(() => {
      img.setAttribute("src", src);
      img.classList.remove("is-swapping");
    }, 250); // must match the .img-main opacity transition duration
  }

  function modifyPopupContent(step = 0) {
    const question = QUESTIONS[step];

    if (!question) {
      throw new Error("No question found.");
    }

    const popupContent = $(".help-me-choose-content");
    popupContent.attr("data-step", step);

    // Cascade: which answers survive the earlier choices, and the first one
    // we can safely fall back to when nothing (valid) is selected yet.
    const eligibility = question.answers.map((ans) =>
      isEligible(step, ans.title),
    );
    const firstEligibleIdx = eligibility.findIndex(Boolean);

    // Drop any previously-stored pick that is no longer eligible (e.g. the
    // user went back and changed the size, invalidating a later choice).
    const selectedTitles = (SELECTED[step] || []).filter((title) => {
      const i = question.answers.findIndex((a) => a.title === title);
      return i !== -1 && eligibility[i];
    });
    const activeAns =
      question.answers.find((ans) => selectedTitles.includes(ans.title)) ||
      question.answers[firstEligibleIdx] ||
      question.answers[0];

    // Build the shell once — fixed header + footer, a scrolling body between.
    // .img-main is kept across steps so setMainImage() can cross-fade it; its
    // absence also signals we're arriving from the results view and need to
    // rebuild the whole shell.
    if (!popupContent.find(".img-main").length) {
      popupContent.html(`
        <div class="help-me-choose-header">
          <progress max="5" class="help-me-choose-progress"></progress>
          <p>Step <span class="step-number"></span> of 5</p>
        </div>
        <div class="help-me-choose-body">
          <img class="img-main" alt="" width="" height="" />
        </div>
        <div class="help-me-choose-nav"></div>
      `);
    }

    popupContent.find(".help-me-choose-progress").attr("value", step + 1);
    popupContent.find(".step-number").text(step + 1);

    setMainImage(imageFor(step, activeAns));

    // Refill the scrolling body, preserving the persistent image element.
    const body = popupContent.find(".help-me-choose-body");
    body.children().not(".img-main").remove();
    body.append(`
      <div class="help-me-choose-question">
        <h2>${question.title}</h2>
        <p>${question.subtitle}</p>
      </div>

      <div class="help-me-choose-selected ${step == 0 ? "hide" : ""}">
        <p>
          You've selected
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
          </svg>
        </p>
        <div class="help-me-choose-selected-list">
          ${Object.keys(SELECTED)
            .filter((key) => Number(key) < step)
            .map((key) =>
              SELECTED[key]
                .map(
                  (x) => `
                    <span class="help-me-choose-selected-item">${x}</span>
                  `,
                )
                .join(""),
            )
            .join("")}
        </div>
      </div>

      <div class="help-me-choose-selection" data-multi="${question.multi || false}">
        ${question.answers
          .map((ans, index) => {
            const eligible = eligibility[index];
            const isSelected =
              eligible &&
              (selectedTitles.length
                ? selectedTitles.includes(ans.title)
                : index === firstEligibleIdx);
            return `
              <div class="option ${isSelected && step < 4 ? "selected" : ""} ${eligible ? "" : "disabled"}" data-image="${imageFor(step, ans)}" ${eligible ? "" : 'aria-disabled="true"'}>
                <div class="option-icon">
                  <img src="${ans.icon || PLACEHOLDER_ICON}" alt="" width="50px" height="50px" />
                </div>
                <p class="option-title">${ans.title}</p>
                <p class="option-desc">${eligible ? ans.description : disabledNote(step)}</p>
              </div>
            `;
          })
          .join("")}
      </div>
    `);

    // Refill the fixed footer (Back / Next).
    popupContent.find(".help-me-choose-nav").html(`
      <button class="btn-prev ${step == 0 ? "invisible" : ""}">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
        </svg>
        Back
      </button>

      <button class="btn-next">
        ${step == 4 ? "Show Results" : "Next"}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
        </svg>
      </button>
    `);
  }

  // Fetched product JSON for the current results, so the tv-item click
  // handler can look a product up by handle and re-render its media.
  let RESULT_PRODUCTS = [];

  function mediaHtmlFor(tvJson) {
    return `
      <img class="image-main" src="${tvJson.featured_image}" alt="" width="" height="" />
      <div class="image-list">
        ${tvJson.media
          .map((x, i) => {
            return `
              <div class="image-item ${i == 0 ? "active" : ""}">
                <img src="${x.src}" alt="${x.alt}" width="" height="" class="${x.alt}" />
              </div>
            `;
          })
          .join("")}
      </div>
    `;
  }

  // ── Right-column (tv-info) helpers ──────────────────────────────────────
  // Money in cents → "$1,799" (no decimals, to match the product page).
  function fmtMoney(cents) {
    return "$" + Math.round(cents / 100).toLocaleString("en-US");
  }

  // The variant whose price/compare-at drive the panel: the default (first
  // priced) variant, mirroring what the product page shows by default.
  function primaryVariant(tvJson) {
    return tvJson.variants.find((v) => v.price > 0) || tvJson.variants[0];
  }

  // Pull "4K AI Google TV" and "2026 Model" out of the full product title.
  function subtitleFrom(title) {
    const panel = (title.match(/((?:8K|4K)[\w\s\-+]*?Google TV)/i) || [])[1];
    const year = (title.match(/\b20\d{2}\s*Model\b/i) || [])[0];
    return { panel: panel && panel.trim(), year };
  }

  // The reviews.rating metafield may arrive as a number, a numeric string,
  // an object, or a JSON string — normalise to { value, count } or null.
  function ratingFrom(meta) {
    if (!meta || meta.rating == null) return null;
    let r = meta.rating;
    if (typeof r === "string" && r.trim().startsWith("{")) {
      try {
        r = JSON.parse(r);
      } catch (e) {}
    }
    if (r && typeof r === "object") r = r.value;
    const value = parseFloat(r);
    if (!isFinite(value)) return null;
    return { value, count: Number(meta.rating_count) || 0 };
  }

  function starsHtml(value) {
    let out = "";
    for (let i = 1; i <= 5; i++) {
      out += `<span class="tv-star ${i <= Math.round(value) ? "filled" : ""}">★</span>`;
    }
    return out;
  }

  // Small voucher/tag glyph shown next to the discounted price.
  const VOUCHER_TAG_ICON = `<svg class="tv-voucher-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M2 2h9l11 11-9 9L2 11V2Zm5 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/></svg>`;

  // Feature title → { title, description, icon } from the multi-select
  // features question, so each matched TV's features render as labelled tiles.
  const FEATURE_INFO = (QUESTIONS.find((q) => q.multi)?.answers || []).reduce(
    (acc, a) => {
      acc[a.title] = a;
      return acc;
    },
    {},
  );

  function featuresHtml(matchedTv) {
    const features = (matchedTv && matchedTv.features) || [];
    if (!features.length) return "";
    return `
      <div class="tv-features">
        ${features
          .map((title) => {
            const info = FEATURE_INFO[title] || {};
            return `
              <div class="tv-feature">
                <div class="tv-feature-icon">
                  <img src="${info.icon || PLACEHOLDER_ICON}" alt="" />
                </div>
                <p class="tv-feature-title semibold fs-12">${title}</p>
                ${info.description ? `<p class="tv-feature-desc subdued fs-10">${info.description}</p>` : ""}
              </div>
            `;
          })
          .join("")}
      </div>
    `;
  }

  function infoHtmlFor(tvJson) {
    const matchedTv = TVS.flatMap((series) => series.list).find(
      (tv) => tv.handle === tvJson.handle,
    );

    const meta = tvJson._meta || {};
    const variant = primaryVariant(tvJson);

    const regularCents = variant.price; // large red price
    const compareCents = variant.compare_at_price || 0; // struck-through price
    const deduction = parseFloat(meta.voucher_deduction) || 0; // dollars off
    const badgeCents = Math.max(regularCents - deduction * 100, 0); // voucher price

    const discountBase = compareCents || regularCents;
    const discountPct = discountBase
      ? Math.round(((discountBase - badgeCents) / discountBase) * 100)
      : 0;

    const badgeDollars = badgeCents / 100;
    const grab = Math.round(badgeDollars / 4);
    const atome = Math.round(badgeDollars / 3);

    const title = matchedTv ? matchedTv.title : tvJson.title;
    const { panel, year } = subtitleFrom(tvJson.title);
    const rating = ratingFrom(meta);

    return `
      <p class="tv-title bold fs-18">${title}</p>
      ${panel ? `<p class="tv-subtitle subdued fs-14">${panel}</p>` : ""}
      ${year ? `<p class="tv-model subdued fs-12">${year}</p>` : ""}

      <div class="tv-meta">
        ${
          rating
            ? `<span class="tv-rating">
                ${starsHtml(rating.value)}
                <span class="fs-12">(${rating.value.toFixed(1)}) ${rating.count} reviews</span>
              </span>`
            : ""
        }
        ${
          tvJson.available
            ? `<span class="tv-stock in-stock fs-12">In Stock!</span>`
            : `<span class="tv-stock out-stock fs-12">Out of stock</span>`
        }
      </div>

      <div class="tv-pricing">
        <span class="tv-price-regular bold text-red fs-20">${fmtMoney(regularCents)}</span>
        ${
          deduction
            ? `<span class="tv-price-badge">${VOUCHER_TAG_ICON}${fmtMoney(badgeCents)}</span>`
            : ""
        }
        ${
          compareCents
            ? `<div class="tv-price-compare">
                <s class="subdued">${fmtMoney(compareCents)}</s>
                ${discountPct > 0 ? `<span class="tv-discount text-red">Up to ${discountPct}% Off*!</span>` : ""}
              </div>`
            : ""
        }
        ${
          deduction
            ? `<div class="tv-installments subdued fs-12">
                4x $${grab.toLocaleString("en-US")} w/ <img src="//cdn.shopify.com/s/files/1/1595/6213/files/grab-icon.png?v=1659153464" /> or 3x $${atome.toLocaleString("en-US")} w/ <img src="//cdn.shopify.com/s/files/1/1595/6213/files/atome-img.png?v=1659153464" />
              </div>`
            : ""
        }
      </div>

      ${featuresHtml(matchedTv)}

      <div class="d-flex flex-col gap-6 tv-actions">
        <a href="javascript:void(0)" class="btn btn-primary quiz-atc custom-atc" data-title="${tvJson.title}" data-variant="${variant.id}" data-bundled-variants="${bundledVariants}">
          Add To Cart${deduction ? ` ${VOUCHER_TAG_ICON}${fmtMoney(badgeCents)}` : ""}
        </a>
        <a href="${tvJson.url}" class="btn quiz-learn-more">Learn More</a>
      </div>
    `;
  }

  // Fetch a product's metafields via the alternate JSON template
  // (templates/product.metafields.liquid) — exposes the voucher promo
  // deduction and JudgeMe rating/review count the .js endpoint omits.
  function fetchMeta(handle) {
    return fetch(`/products/${handle}?view=metafields`)
      .then((res) => res.json())
      .catch(() => null);
  }

  function showResults() {
    // ── Match TVs against the user's selections ──
    // Same predicate that drives the cascade, so the results always agree
    // with the funnel: room is context only, size/use/picture match exactly,
    // features match on ANY ticked feature.
    const matchedTvs = [];

    TVS.forEach((series) => {
      series.list.forEach((tv) => {
        if (tvSatisfies(tv, SELECTED)) {
          matchedTvs.push({ series: series.series, ...tv });
        }
      });
    });

    const popupContent = $(".help-me-choose-content");
    popupContent.attr("data-step", "final");

    // Same three regions as the steps: fixed header + footer, scrolling body.
    popupContent.html(`
      <div class="help-me-choose-header">
        <p>FINAL MATCHES</p>
        <h2>Here's your ideal TV.</h2>
      </div>

      <div class="help-me-choose-body">
        <div class="help-me-choose-results"></div>
      </div>

      <div class="help-me-choose-nav">
        <div class="help-me-choose-selected">
          <p>
            You've selected
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
            </svg>
          </p>
          <div class="help-me-choose-selected-list">
            ${Object.keys(SELECTED)
              .map((key) =>
                SELECTED[key]
                  .map(
                    (x) => `
                      <span class="help-me-choose-selected-item">${x}</span>
                    `,
                  )
                  .join(""),
              )
              .join("")}
          </div>
        </div>
        <button class="btn-reset">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          Retake quiz
        </button>
      </div>
    `);

    // ── Fetch each matched product's JSON, then render the list ──
    const resultsContainer = popupContent.find(".help-me-choose-results");
    const NO_MATCH_HTML = `
      <div class="tv-empty">
        <p class="bold fs-14">No matching TVs found</p>
        <p class="subdued fs-14">Try fewer features or a different size, then retake the quiz to see more options.</p>
      </div>
    `;

    if (matchedTvs.length) {
      // Show a loading spinner while the matched products are fetched.
      resultsContainer.append(`
        <div class="tv-loading">
          <span class="tv-loading-spinner" role="status" aria-label="Loading"></span>
          <p class="subdued fs-14">Finding your perfect match…</p>
        </div>
      `);

      Promise.all(
        matchedTvs.map((tv) => {
          // If this product borrows its reviews from another product, fetch
          // that product's metafields too and splice in only its rating.
          const reviewHandle = REVIEW_SOURCE_HANDLES[tv.handle];
          return Promise.all([
            fetch(`/products/${tv.handle}.js`).then((res) => res.json()),
            fetchMeta(tv.handle),
            reviewHandle ? fetchMeta(reviewHandle) : Promise.resolve(null),
          ])
            .then(([product, meta, reviewMeta]) => {
              const merged = reviewMeta
                ? {
                    ...meta,
                    rating: reviewMeta.rating,
                    rating_count: reviewMeta.rating_count,
                  }
                : meta;
              return { ...product, _meta: merged };
            })
            .catch((err) => {
              console.error(
                `Help Me Choose — failed to fetch ${tv.handle}:`,
                err,
              );
              return null;
            });
        }),
      ).then((matchedTvsJson) => {
        resultsContainer.find(".tv-loading").remove();
        resultsContainer.html(`
          <div class="tv-list"></div>
          <div class="tv-media"></div>
          <div class="tv-info"></div>
        `);

        RESULT_PRODUCTS = matchedTvsJson.filter(Boolean);

        if (!RESULT_PRODUCTS.length) {
          resultsContainer.html(NO_MATCH_HTML);
          return;
        }

        const itemsHtml = RESULT_PRODUCTS.map((x, i) => {
          const matchedTv = matchedTvs.find((tv) => tv.handle === x.handle);

          let lowestPrice = x.price_max;
          x.variants.forEach((v) => {
            if (v.price > 0 && x.price < lowestPrice) {
              lowestPrice = v.price;
            }
          });

          return `
            <div class="tv-item ${i == 0 ? "selected" : ""}" data-handle="${x.handle}">
              <div class="column col-left">
                <div class="selector">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                  </svg>
                </div>
                <img class="remove-bg" src="${x.featured_image}" alt="${x.title}" />
              </div>
              <div class="column col-right">
                <p class="semibold fs-14">${matchedTv ? matchedTv.series : ""}</p>
                <p class="subdued fs-10">${matchedTv.inches}″ · ${matchedTv.picture[0].match(/\(([^)]+)\)/)?.[1] ?? matchedTv.picture[0]}</p>
                <p class="bold text-red">${theme.Currency.formatMoney(lowestPrice)}</p>
              </div>
            </div>
          `;
        }).join("");

        popupContent.find(".tv-list").html(itemsHtml);

        if (RESULT_PRODUCTS.length) {
          popupContent.find(".tv-media").html(mediaHtmlFor(RESULT_PRODUCTS[0]));
          popupContent.find(".tv-info").html(infoHtmlFor(RESULT_PRODUCTS[0]));
        }
      });
    } else {
      resultsContainer.html(NO_MATCH_HTML);
    }
  }

  function resetPopupContent() {
    modifyPopupContent(0);
  }

  function resetSelected() {
    Object.keys(SELECTED).forEach((key) => {
      SELECTED[key] = [];
    });
  }

  function addHorizontalScroll() {
    const popup = $(".help-me-choose-popup");

    let isDown = false;
    let startX;
    let scrollLeft;
    let isDragging = false;

    popup.on(
      "mousedown",
      ".help-me-choose-selection[data-multi='true']",
      function (e) {
        isDown = true;
        isDragging = false;
        startX = e.pageX - this.offsetLeft;
        scrollLeft = this.scrollLeft;
      },
    );
    popup.on(
      "mouseleave mouseup",
      ".help-me-choose-selection[data-multi='true']",
      function () {
        isDown = false;
        $(".help-me-choose-selection").removeClass("is-dragging");
      },
    );
    popup.on(
      "mousemove",
      ".help-me-choose-selection[data-multi='true']",
      function (e) {
        if (!isDown) return;
        e.preventDefault();

        const x = e.pageX - this.offsetLeft;
        const walk = (x - startX) * 1.5;

        if (Math.abs(x - startX) > 5) {
          isDragging = true;
          $(".help-me-choose-selection").addClass("is-dragging");
        }

        this.scrollLeft = scrollLeft - walk;
      },
    );
    popup.on(
      "click",
      ".help-me-choose-selection[data-multi='true'] .option",
      function (e) {
        if (isDragging) {
          e.stopImmediatePropagation();
          isDragging = false;
        }
      },
    );

    // Mouse wheel: translate vertical wheel movement into horizontal scroll.
    // Pure-horizontal input (e.g. a trackpad swipe) has no deltaY, so we let
    // the browser handle it natively.
    popup.on(
      "wheel",
      ".help-me-choose-selection[data-multi='true']",
      function (e) {
        if (this.scrollWidth <= this.clientWidth) return; // nothing to scroll
        const delta = e.originalEvent.deltaY;
        if (!delta) return;
        this.scrollLeft += delta;
        e.preventDefault();
      },
    );
  }

  function addEvents() {
    const popup = $(".help-me-choose-popup");

    popup.on("click", ".option", function (e) {
      if ($(this).hasClass("disabled")) return;

      const isMulti =
        $(this).closest(".help-me-choose-selection").attr("data-multi") ==
        "true";

      if (isMulti) {
        $(this).toggleClass("selected");
      } else {
        $(this).siblings().removeClass("selected");
        $(this).addClass("selected");

        const image = $(this).attr("data-image");
        if (image) {
          setMainImage(image);
        }
      }
    });

    popup.on("click", ".btn-next", function () {
      const curStep = Number(
        popup.find(".help-me-choose-content").attr("data-step"),
      );

      const nextStep = curStep + 1;

      SELECTED[curStep] = $(
        ".help-me-choose-selection .option.selected .option-title",
      )
        .map(function () {
          return $(this).text();
        })
        .get();

      if (nextStep == 5) {
        showResults();
      } else {
        try {
          modifyPopupContent(nextStep);
        } catch (error) {
          console.error(error);
          SELECTED[curStep] = [];
        }
      }
    });

    popup.on("click", ".btn-prev", function () {
      const curStep = Number(
        popup.find(".help-me-choose-content").attr("data-step"),
      );
      if (curStep == 0) return;

      const prevStep = curStep - 1;

      modifyPopupContent(prevStep);
    });

    popup.on("click", ".btn-reset", function () {
      resetSelected();
      resetPopupContent();
    });

    popup.on("click", ".tv-list .tv-item", function () {
      $(this).siblings(".selected").removeClass("selected");
      $(this).addClass("selected");

      const handle = $(this).attr("data-handle");
      const product = RESULT_PRODUCTS.find((p) => p.handle === handle);
      if (product) {
        popup.find(".tv-media").html(mediaHtmlFor(product));
        popup.find(".tv-info").html(infoHtmlFor(product));
      }
    });

    popup.on("click", ".tv-media .image-item", function () {
      $(this).siblings(".active").removeClass("active");
      $(this).addClass("active");
      const src = $(this).children().first().attr("src");
      $(this).closest(".tv-media").find(".image-main").attr("src", src);
    });
  }
})();
